import { create } from 'zustand';
import { AxiosError } from 'axios';
import { applicationService } from 'src/services/application';
import {
  Application,
  SubscriptionPlan,
} from 'src/services/interfaces/application';
import { useInsuranceStore } from './insurance';
// import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { DEFAULT_TIER } from '@features/onboarding/constants';
import { Tier } from 'src/services/interfaces/tier';
import { getAddress } from 'src/services/profile';
// import { FormStepID } from '@features/onboarding/enums/formSections.enum';
import { useAIStore } from './ai';

const PHQ_PROGRESS_STEP = 5;

interface ApplicationState {
  applications: Application[];
  isLoading: boolean;
  isCreated: boolean;
  error: string | null;
  status?: 'created' | 'pending' | 'approved' | 'rejected';
  currentApplicationId?: number | string;
  isMainPlanRejected?: boolean;
  activeSubscriptionPlans: SubscriptionPlan[];
  activeCategories: string[];
  isAllRejected: boolean;
  currentApplicationStatus: 0 | 1;
  signedAt: string | null;
  submittedAt?: string | null;
  hasCompletedApplication: boolean;
  isNewApplicant: boolean;
  hasReachedPhq: boolean | null;
  createApplication: (data: Application) => Promise<void>;
  updateApplication: (data: Application) => Promise<void>;
  submitApplication: (
    id: number,
    data: { paymentMethodId?: string }
  ) => Promise<void>;
  acceptApplication: () => Promise<void>;
  rejectApplication: () => Promise<void>;
  getCurrentApplication: () => Promise<void>;
  updateApplicationById: (
    id: number | string,
    data: Application
  ) => Promise<void>;
  setIsMainPlanRejected: (isMainPlanRejected: boolean) => void;
  setIsAllRejected: (isAllRejected: boolean) => void;
  fetchActiveSubscriptionPlans: () => Promise<void>;
  updateTierFromApplicationState: () => Promise<void>;
  tier: string;
  acceptedTier: Tier | null;
  phqUnderReviewAt: string | null;
  phqAcceptedAt: string | null;
  setAcceptedTier: (tier: Tier) => void;
  clearMedicalPlan: () => Promise<void>;
}

export const useApplicationStore = create<ApplicationState>((set, get) => ({
  applications: [],
  isLoading: false,
  isCreated: false,
  tier: DEFAULT_TIER,
  acceptedTier: null,
  submittedAt: null,
  isMainPlanRejected: false,
  activeSubscriptionPlans: [],
  error: null,
  activeCategories: [],
  isAllRejected: false,
  currentApplicationStatus: 0,
  signedAt: null,
  phqUnderReviewAt: null,
  hasCompletedApplication: false,
  isNewApplicant: false,
  hasReachedPhq: null,
  phqAcceptedAt: null,

  setAcceptedTier: (tier: Tier) => {
    set({ acceptedTier: tier });
  },

  clearMedicalPlan: async () => {
    get().updateApplication({
      ...get().applications[0],
      plans: [],
    });
  },

  // Устанавливаем флаг, опираясь на уже загруженные activeSubscriptionPlans.
  // Никаких сетевых запросов здесь не выполняем, чтобы избежать лишних вызовов API.
  setIsMainPlanRejected: (rejected: boolean) => {
    const { activeSubscriptionPlans } = useApplicationStore.getState();
    const fullPlans = useInsuranceStore.getState().plans;

    const hasMajorMedical = (() => {
      if (activeSubscriptionPlans.length === 0) return false;
      const activeIds = activeSubscriptionPlans.map((p) => p.planId);
      return fullPlans.some(
        (plan) =>
          activeIds.includes(plan.id) && plan.category === 'Major Medical'
      );
    })();

    set({ isMainPlanRejected: hasMajorMedical || rejected });
  },
  setIsAllRejected: (isAllRejected: boolean) => {
    set({ isAllRejected });
  },

  fetchActiveSubscriptionPlans: async () => {
    try {
      const activeSubscriptionPlans =
        await applicationService.getActiveSubscriptionPlans();
      const fullPlans = useInsuranceStore.getState().plans;
      const activeCategories = fullPlans
        .filter((plan) =>
          activeSubscriptionPlans
            .map((item) => item.planId)
            .includes(plan?.id || 0)
        )
        .map((item) => item.category);
      const allCategories = fullPlans.map((item) => item.category);
      // isAllRejected = true только если пользователь купил планы из ВСЕХ доступных категорий
      const isAllRejected =
        allCategories.length > 0 &&
        allCategories.every((category) => activeCategories.includes(category));
      set({ activeSubscriptionPlans, activeCategories, isAllRejected });
    } catch (error) {
      console.error('Failed to fetch active subscription plans:', error);
    }
  },

  getCurrentApplication: async () => {
    set({ isLoading: true, error: null });
    let hasCompletedApplication = false;
    try {
      const applicationsList = await applicationService.getApplicationsList();
      hasCompletedApplication = applicationsList.some(
        (application) => application.status === 1
      );
      set({
        hasCompletedApplication,
        isNewApplicant: !hasCompletedApplication,
        hasReachedPhq: applicationsList.some(
          (application) => (application.progress ?? 0) >= PHQ_PROGRESS_STEP
        ),
      });
    } catch (listError) {
      console.error('Failed to fetch applications list:', listError);
      set({
        hasCompletedApplication: false,
        isNewApplicant: true,
        hasReachedPhq: false,
      });
    }

    try {
      const applicationData = await applicationService.getApplications();
      if (applicationData.status === 1) {
        useApplicationStore
          .getState()
          .createApplication({ ...applicationData.payload, plans: [] });
        useApplicationStore.getState().fetchActiveSubscriptionPlans();
      }
      const { payload, id, status, signature } = applicationData;
      const currentPage = useAIStore.getState().currentPage;
      const shouldFetchAddress =
        currentPage === 'welcome' || currentPage === 'Finish';

      let fetchedAddress: Awaited<ReturnType<typeof getAddress>> | null = null;

      if (shouldFetchAddress) {
        fetchedAddress = await getAddress();
        console.log('address', fetchedAddress);
      }

      if (payload && fetchedAddress) {
        payload.address = fetchedAddress;
      }
      if (id) {
        set({ currentApplicationId: id });
      }

      // Set signedAt from signature data
      const signedAt = signature?.signedAt || null;
      set({ signedAt });

      if (!payload) {
        set({ isLoading: false, status: 'created' });
      } else {
        // payload
        let activeSubscriptionPlans: SubscriptionPlan[] = [];
        if (payload.plans && payload.plans.length > 0) {
          activeSubscriptionPlans =
            await applicationService.getActiveSubscriptionPlans();
          payload.plans = payload.plans.filter(
            (plan) =>
              !activeSubscriptionPlans
                .map((item) => item.planId)
                .includes(plan.id)
          );
        }
        console.log('payload', payload);
        set({
          applications: [payload],
          submittedAt: applicationData?.submittedAt,
          isLoading: false,
          status: 'created',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        set({
          applications: [],
          currentApplicationId: undefined,
          isLoading: false,
          status: undefined,
          error: null,
          signedAt: null,
          submittedAt: null,
        });
        return;
      }
      set({
        error: 'Failed to fetch current application.',
        isLoading: false,
      });
    }
  },

  createApplication: async (data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const application = await applicationService.createApplication(data);
      set({
        currentApplicationId: application.id,
        status: 'created',
        applications: [
          ...useApplicationStore.getState().applications,
          application,
        ],
        isLoading: false,
        isCreated: true,
      });
      // const { activeSubscriptionPlans } = useApplicationStore.getState();
      // if(useApplicationStore.getState().isMainPlanRejected || activeSubscriptionPlans.length > 0){
      //   window.location.reload();
      // }
    } catch (error) {
      set({ error: 'Failed to create application.', isLoading: false });
    }
  },

  updateApplication: async (data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const application = await applicationService.updateApplication(data);
      set({
        applications: useApplicationStore
          .getState()
          .applications.map((item) => (item === data ? application : item)),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update application.', isLoading: false });
    }
  },

  updateApplicationById: async (id: number | string, data: Application) => {
    set({ isLoading: true, error: null });
    try {
      const isMainPlanRejected =
        useApplicationStore.getState().isMainPlanRejected;
      console.log('isMainPlanRejected', isMainPlanRejected);
      if (isMainPlanRejected) {
        const fullPlans = useInsuranceStore.getState().plans;
        const newPlans = [];
        if (fullPlans.length > 0) {
          const dataPlans = [...data.plans];
          for (const plan of dataPlans) {
            const myMajorPlan = fullPlans
              .find((p) => p.category === 'Major Medical' && p.id === plan.id)
              ?.prices.find((item) => item.id === plan.priceId);
            if (!myMajorPlan) {
              newPlans.push(plan);
            }
          }
          data.plans = dataPlans;
        }
        data.plans = newPlans;
      }
      await applicationService.updateApplicationById(id, data);
      const activeSubscriptionPlans =
        await applicationService.getActiveSubscriptionPlans();
      set({ activeSubscriptionPlans });
    } catch (error) {
      set({ error: 'Failed to update application by id.', isLoading: false });
    } finally {
      await useApplicationStore.getState().getCurrentApplication();
    }
  },

  submitApplication: async (id: number, data: { paymentMethodId?: string }) => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.submitApplication(id, data);
      await useApplicationStore.getState().fetchActiveSubscriptionPlans();
      set({
        isLoading: false,
        hasCompletedApplication: true,
        isNewApplicant: false,
      });
    } catch (error) {
      set({ error: 'Failed to submit application.', isLoading: false });
    }
  },

  acceptApplication: async () => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.acceptApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to accept application.', isLoading: false });
    }
  },

  rejectApplication: async () => {
    set({ isLoading: true, error: null });
    try {
      await applicationService.rejectApplication();
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Failed to reject application.', isLoading: false });
    }
  },

  updateTierFromApplicationState: async () => {
    try {
      const applicationState = await applicationService.applicationState();
      console.log('applicationState', applicationState);
      set({ phqUnderReviewAt: applicationState.phqUnderReviewAt, phqAcceptedAt: applicationState.phqAcceptedAt });
      if (applicationState.acceptedTier) {
        set({
          tier: applicationState.acceptedTier,
          acceptedTier: applicationState.acceptedTier,
        });
      } else {
        const applicationData = await applicationService.getApplications();
        const { payload, id, status, signature } = applicationData;
        if (payload && payload.plans.length > 0) {
          console.log('currentApplication', payload);
          await useApplicationStore
            .getState()
            .updateApplicationById(id, { ...payload, plans: [] });
        }
        set({ tier: DEFAULT_TIER, acceptedTier: null });
      }
    } catch (error) {
      console.error('Failed to update tier from application state:', error);
      set({ tier: DEFAULT_TIER, acceptedTier: null });
    }
  },
}));
